import { ANCHOR_TYPE, SCHEDULE_METADATA_KEY, PARTICIPANT_SESSION_KEY } from '../constants'
import {
  applyEngineScheduleVersions,
  readSchedulesFromInstrument,
  toScheduleMetadataPayload,
} from '../models/normalizeDraft'
import { createEmptyParticipantSession } from '../models/createEmptyParticipantSession'
import { applyAnchorLocks, resolvePinningFromAnchor } from '../validation/locks'
import { isPinningAnchorType } from '../hooks/anchorConstraints'
import { buildV2InstrumentUpdateBody, instrumentV2Urls, runInstrumentV2AsyncJob } from 'routes/Studies/routes/Study/routes/Instruments/utils/instrumentV2Api'
import { buildV3InstrumentUpdateBody, getInstrumentUpdateUrl } from 'routes/Studies/routes/Study/routes/Instruments/utils/instrumentV3Api'
import request, { generateNotyMessage } from 'utils/request'
import { actions as notyActions } from 'layouts/ErrorBox'
import { logSentryEvent } from 'utils/sentry'
import { setInstrument } from 'routes/Studies/routes/Study/routes/Instruments/routes/Instrument/modules/Instrument'

const LOG_TAG = 'schedulingEngine'

const mergeScheduleMetadata = (instrument, schedules) => {
  const baseline = { ...(instrument?.metadata || {}) }
  delete baseline.visibility
  delete baseline.visibilityRanges
  return {
    ...instrument,
    metadata: {
      ...baseline,
      [SCHEDULE_METADATA_KEY]: toScheduleMetadataPayload(schedules),
    },
  }
}

export const readEngineSchedules = instrument => readSchedulesFromInstrument(instrument)

/** True when the instrument already has at least one contract schedule persisted on the server. */
export const hasPersistedEngineSchedules = instrument => {
  const schedule = instrument?.metadata?.schedule
  return Array.isArray(schedule) && schedule.length > 0 && Array.isArray(schedule[0]?.deploy?.anchors)
}

/** Legacy instrument-level participant session (Properties step when flag is off). */
export const readParticipantSession = instrument =>
  createEmptyParticipantSession(instrument?.metadata?.[PARTICIPANT_SESSION_KEY] || {})

const fetchInstrumentMetadata = (studyID, instrumentID) =>
  request({
    method: 'GET',
    url: instrumentV2Urls.one(studyID, instrumentID),
    hasLoader: false,
    success: json => json,
  })

export const lookupInstrumentPinning = async ({ studyID, instrumentId, entityLookup, dispatch }) => {
  if (!instrumentId) return null
  try {
    const json = await dispatch(fetchInstrumentMetadata(studyID, instrumentId))
    const { schedules } = readEngineSchedules(json || json?.instrument || {})
    const firstPinning = (schedules[0]?.anchors || []).find(anchor => isPinningAnchorType(anchor.type))
    return firstPinning ? resolvePinningFromAnchor(firstPinning, entityLookup) : null
  } catch (error) {
    logSentryEvent({
      message: `${LOG_TAG}.instrumentLookup.failed`,
      level: 'warning',
      tags: { feature: LOG_TAG },
      contexts: { schedulingEngine: { instrumentId, studyID } },
      error,
    })
    return null
  }
}

export const collectInstrumentPinning = async ({ studyID, schedules, entityLookup, dispatch }) => {
  const map = {}
  const ids = new Set()
  schedules.forEach(schedule => {
    ;(schedule.anchors || []).forEach(anchor => {
      if (anchor.type === ANCHOR_TYPE.instrument && anchor.instanceId) ids.add(String(anchor.instanceId))
    })
  })
  await Promise.all(
    [...ids].map(async id => {
      map[id] = await lookupInstrumentPinning({ studyID, instrumentId: id, entityLookup, dispatch })
    }),
  )
  return map
}

export const applyLocksForSave = (schedules, entityLookup, instrumentPinningMap) =>
  schedules.map(schedule => {
    const instrumentAnchor = (schedule.anchors || []).find(anchor => anchor.type === ANCHOR_TYPE.instrument)
    const instrumentPinning = instrumentAnchor ? instrumentPinningMap[String(instrumentAnchor.instanceId)] : null
    return applyAnchorLocks(schedule, entityLookup, instrumentPinning)
  })

export const saveEngineSchedules = ({
  studyID,
  instrument,
  schedules,
  hasSchedulingEngine,
  activeScheduleId,
}) => dispatch => {
  const versionedSchedules = applyEngineScheduleVersions(schedules, instrument, { activeScheduleId })
  const nextInstrument = mergeScheduleMetadata(instrument, versionedSchedules)
  return dispatch(
    request({
      method: 'PUT',
      url: getInstrumentUpdateUrl(studyID, instrument.id, hasSchedulingEngine),
      hasLoader: true,
      body: JSON.stringify(
        hasSchedulingEngine
          ? buildV3InstrumentUpdateBody(nextInstrument)
          : buildV2InstrumentUpdateBody(nextInstrument),
      ),
      failMessage: 'Saving schedule failed',
      successMessage: 'Schedule saved',
      success: () => {
        dispatch(setInstrument(nextInstrument))
        return nextInstrument
      },
    }),
  ).catch(error => {
    logSentryEvent({
      message: `${LOG_TAG}.save.failed`,
      level: 'error',
      tags: { feature: LOG_TAG },
      contexts: { schedulingEngine: { studyID, instrumentId: instrument?.id } },
      error,
    })
    dispatch(
      notyActions.showError({
        text: generateNotyMessage(error?.message || 'Saving schedule failed', false),
      }),
    )
    return Promise.reject(error)
  })
}

export const deleteEngineSchedule = ({
  studyID,
  instrumentID,
  scheduleId,
  studyVersionID,
  hasStudyPromotion,
}) => dispatch => {
  const query = hasStudyPromotion && studyVersionID ? `?study_version_id=${studyVersionID}` : ''
  return dispatch(
    runInstrumentV2AsyncJob({
      studyID,
      successMessage: 'Schedule deleted successfully',
      failMessage: 'Failed to delete schedule',
      startRequest: request({
        method: 'DELETE',
        url: instrumentV2Urls.schedule(studyID, instrumentID, scheduleId, query),
        contentType: null,
        hasLoader: true,
        skipNotySuccess: true,
        success: json => json,
        fail: (_response, content) => Promise.reject(new Error(content?.message || 'Failed to delete schedule')),
      }),
    }),
  )
    .then(() =>
      dispatch(
        request({
          method: 'GET',
          url: instrumentV2Urls.one(studyID, instrumentID),
          hasLoader: false,
          success: json => json,
        }),
      ),
    )
    .then(json => {
      const nextInstrument = json?.instrument || json
      if (nextInstrument) {
        dispatch(setInstrument(nextInstrument))
      }
      return nextInstrument
    })
    .catch(error => {
      logSentryEvent({
        message: `${LOG_TAG}.delete.failed`,
        level: 'error',
        tags: { feature: LOG_TAG },
        contexts: { schedulingEngine: { studyID, instrumentId: instrumentID, scheduleId } },
        error,
      })
      dispatch(
        notyActions.showError({
          text: generateNotyMessage(error?.message || 'Failed to delete schedule', false),
        }),
      )
      return Promise.reject(error)
    })
}
